import { effect, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { EMPTY, merge } from 'rxjs';
import {
  DEFAULT_LANGUAGE,
  LANGUAGE_CODES,
  SUPPORTED_LANGUAGES,
  SupportedLanguage,
} from './i18n.constants';

@Injectable({
  providedIn: 'root',
})
export class I18nStore {
  private readonly translateService = inject(TranslateService);
  private readonly _currentLanguage = signal<SupportedLanguage>(DEFAULT_LANGUAGE);
  private readonly _translationRevision = signal(0);

  readonly currentLanguage = this._currentLanguage.asReadonly();
  readonly translationRevision = this._translationRevision.asReadonly();
  private readonly _supportedLanguages = signal<readonly SupportedLanguage[]>(SUPPORTED_LANGUAGES);
  readonly supportedLanguages = this._supportedLanguages.asReadonly();

  constructor() {
    void this.translateService.setFallbackLang(LANGUAGE_CODES[DEFAULT_LANGUAGE]);

    effect(() => {
      this.translateService.use(LANGUAGE_CODES[this._currentLanguage()]);
    });

    merge(
      this.translateService.onLangChange ?? EMPTY,
      this.translateService.onTranslationChange ?? EMPTY,
    )
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this._translationRevision.update((value) => value + 1);
      });
  }

  setLanguage(language: SupportedLanguage): void {
    this._currentLanguage.set(language);
  }

  instant(key: string, params?: Record<string, string | number>): string {
    this._translationRevision();
    return this.translateService.instant(key, params);
  }
}